// Copyright (c) 2026 aiApas LLC. All Rights Reserved.
// GuardRok — Proprietary and Confidential. Patent Pending.
// Unauthorized use or distribution is prohibited.

import { Link } from 'react-router-dom'
import { GuardRokIcon } from '../components/GuardRokLogo'

const BASE = 'https://guardrok.com'

const SECTIONS = [
  {
    title: 'Main',
    links: [
      {
        label: 'Home',
        path: '/',
        desc: "GuardRok AI Firewall — protect your organisation's AI usage",
      },
      { label: 'Pricing', path: '/pricing', desc: 'Starter, Professional, and Enterprise plans' },
    ],
  },
  {
    title: 'Product',
    links: [
      { label: 'Sign In', path: '/login', desc: 'Log in to your GuardRok dashboard' },
      { label: 'Sign Up', path: '/signup', desc: 'Create a free account' },
      { label: 'Enterprise', path: '/enterprise', desc: 'SSO, SCIM, BAA, and enterprise controls' },
      { label: 'Security & Trust', path: '/security', desc: 'How GuardRok protects your data — BAA, DPA, ZK identity' },
      { label: 'Design Partners', path: '/design-partners', desc: '3 founding design partner spots — healthcare, legal, finance' },
    ],
  },
  {
    title: 'Legal',
    links: [
      {
        label: 'Privacy Policy',
        path: '/privacy',
        desc: 'How we collect, use, and protect your data',
      },
      { label: 'Terms of Service', path: '/terms', desc: 'Terms governing use of GuardRok' },
      {
        label: 'Cookie Policy',
        path: '/cookies',
        desc: 'Cookies and tracking technologies we use',
      },
      {
        label: 'Do Not Sell My Information',
        path: '/do-not-sell',
        desc: 'CCPA/CPRA opt-out and California privacy rights',
      },
    ],
  },
]

export default function Sitemap() {
  return (
    <div
      style={{
        minHeight: '100vh',
        background: '#030712',
        color: '#fff',
        fontFamily: 'system-ui, sans-serif',
      }}
    >
      {/* Nav */}
      <nav
        style={{
          padding: '20px 48px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderBottom: '1px solid #0f172a',
          position: 'sticky',
          top: 0,
          background: 'rgba(3,9,18,0.95)',
          backdropFilter: 'blur(12px)',
          zIndex: 100,
        }}
      >
        <Link
          to="/"
          style={{ display: 'flex', alignItems: 'center', gap: 12, textDecoration: 'none' }}
        >
          <GuardRokIcon size={36} />
          <div>
            <div
              style={{
                fontWeight: 800,
                fontSize: 18,
                letterSpacing: '-0.5px',
                lineHeight: 1,
                color: '#fff',
              }}
            >
              Guard<span style={{ color: '#E63946' }}>Rok</span>
              <span
                style={{ fontSize: 8, color: '#6b7280', verticalAlign: 'super', marginLeft: 1 }}
              >
                ™
              </span>
            </div>
            <div
              style={{
                fontSize: 9,
                letterSpacing: '3px',
                color: '#6b7280',
                textTransform: 'uppercase' as const,
                marginTop: 2,
              }}
            >
              AI Firewall
            </div>
          </div>
        </Link>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
          <Link to="/pricing" style={{ color: '#9ca3af', fontSize: 14, textDecoration: 'none' }}>
            Pricing
          </Link>
          <Link to="/login" style={{ color: '#9ca3af', fontSize: 14, textDecoration: 'none' }}>
            Sign in
          </Link>
        </div>
      </nav>

      {/* Content */}
      <div style={{ maxWidth: 900, margin: '0 auto', padding: '64px 40px 96px' }}>
        <h1 style={{ fontSize: 36, fontWeight: 900, letterSpacing: '-1px', marginBottom: 10 }}>
          Sitemap
        </h1>
        <p style={{ color: '#64748b', fontSize: 13, marginBottom: 56, lineHeight: 1.7 }}>
          A complete list of all pages on <strong style={{ color: '#94a3b8' }}>guardrok.com</strong>
          .
        </p>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 40 }}>
          {SECTIONS.map(({ title, links }) => (
            <div key={title}>
              <div
                style={{
                  fontSize: 10,
                  fontWeight: 700,
                  textTransform: 'uppercase' as const,
                  letterSpacing: '2px',
                  color: '#E63946',
                  marginBottom: 16,
                }}
              >
                {title}
              </div>
              <div style={{ display: 'flex', flexDirection: 'column' as const, gap: 12 }}>
                {links.map(({ label, path, desc }) => (
                  <div key={path}>
                    <Link
                      to={path}
                      style={{
                        fontSize: 14,
                        fontWeight: 600,
                        color: '#f1f5f9',
                        textDecoration: 'none',
                        display: 'block',
                        marginBottom: 2,
                      }}
                      onMouseEnter={e => (e.currentTarget.style.color = '#E63946')}
                      onMouseLeave={e => (e.currentTarget.style.color = '#f1f5f9')}
                    >
                      {label}
                    </Link>
                    <div style={{ fontSize: 11, color: '#475569', lineHeight: 1.5 }}>{desc}</div>
                    <div style={{ fontSize: 10, color: '#1f2937', marginTop: 2 }}>
                      {BASE}
                      {path}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Footer */}
      <div
        style={{
          borderTop: '1px solid #0f172a',
          padding: '20px 48px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <div style={{ fontSize: 12, color: '#374151' }}>© 2026 GuardRok™</div>
        <div style={{ display: 'flex', gap: 20 }}>
          <Link to="/privacy" style={{ color: '#374151', fontSize: 12, textDecoration: 'none' }}>
            Privacy
          </Link>
          <Link to="/terms" style={{ color: '#374151', fontSize: 12, textDecoration: 'none' }}>
            Terms
          </Link>
          <Link
            to="/do-not-sell"
            style={{ color: '#374151', fontSize: 12, textDecoration: 'none' }}
          >
            Do Not Sell
          </Link>
        </div>
      </div>
    </div>
  )
}
